import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import "../sections/SectionStyles.css";
import {
  getFileStructure,
  getFolderContents,
} from "../../services/myLibraryService";
import LoadingSpinner from "./LoadingSpinner";
import EmptyState from "./EmptyState";
import FileGridItem from "./FileGridItem";
import { getFileTypeIcon, formatFileSize } from "./fileUtils";

// Helper to build folder tree (only folders, no files)
function buildFolderTree(items, parentId = null) {
  return items
    .filter((item) => item.type === "folder" && item.parentId === parentId)
    .map((item) => ({
      ...item,
      children: buildFolderTree(items, item._id),
    }));
}

// Helper to get breadcrumb path
function getBreadcrumbPath(items, currentId) {
  const path = [];
  let current = items.find((item) => item._id === currentId);

  while (current) {
    path.unshift(current);
    current = items.find((item) => item._id === current.parentId);
  }

  return path;
}

const FolderTree = ({ tree, selectedId, onSelect, expanded, onToggle }) => {
  if (!tree || tree.length === 0) return null;

  return (
    <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
      {tree.map((item) => {
        if (item.type !== "folder") return null;

        const hasChildren = item.children && item.children.length > 0;

        return (
          <li key={item._id}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 12px",
                margin: "2px 0",
                borderRadius: 10,
                cursor: "pointer",
                transition: "all 0.2s ease",
                background:
                  selectedId === item._id
                    ? "rgba(37,99,235,0.08)"
                    : "transparent",
                color: selectedId === item._id ? "#2563eb" : "#64748b",
                fontWeight: selectedId === item._id ? 600 : 400,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item._id);
              }}
            >
              {hasChildren && (
                <span
                  style={{
                    marginRight: 8,
                    fontSize: 16,
                    color: selectedId === item._id ? "#2563eb" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(item._id);
                  }}
                >
                  {expanded[item._id] ? "▼" : "▶"}
                </span>
              )}
              <span style={{ fontSize: 16, marginRight: 8 }}>📁</span>
              <span style={{ flex: 1 }}>{item.name}</span>
            </div>
            {expanded[item._id] && hasChildren && (
              <div style={{ marginLeft: 20 }}>
                <FolderTree
                  tree={item.children}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  expanded={expanded}
                  onToggle={onToggle}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

function getFileIcon(type) {
  switch (type?.toLowerCase()) {
    case "pdf":
      return "📄";
    case "doc":
    case "docx":
      return "📝";
    case "txt":
      return "📃";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "🖼️";
    case "mp3":
    case "wav":
    case "aac":
      return "🎵";
    case "mp4":
    case "avi":
    case "mov":
    case "mkv":
      return "🎥";
    default:
      return "📄";
  }
}

const SelectLibraryComponet = ({ onSelect, onClose }) => {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [currentFolderContents, setCurrentFolderContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);

  // Fetch initial file structure
  useEffect(() => {
    const fetchStructure = async () => {
      try {
        setLoading(true);
        const data = await getFileStructure();
        const itemsArray = Array.isArray(data) ? data : data?.data || [];
        setItems(itemsArray);
        setCurrentFolderContents(itemsArray.filter((item) => !item.parentId));
        setError(null);
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load file structure");
      } finally {
        setLoading(false);
      }
    };

    fetchStructure();
  }, []);

  // Fetch folder contents when a folder is selected
  useEffect(() => {
    const fetchFolderContents = async () => {
      if (!selectedId) {
        setCurrentFolderContents(items.filter((item) => !item.parentId));
        setBreadcrumbPath([]);
        return;
      }

      try {
        setLoading(true);
        const response = await getFolderContents(selectedId);
        setCurrentFolderContents(response.contents);
        // Update breadcrumbs using the path from API response
        if (response.path && Array.isArray(response.path)) {
          setBreadcrumbPath(response.path);
          // Expand all parent folders in the path
          const newExpanded = {};
          response.path.forEach((folder) => {
            newExpanded[folder._id] = true;
          });
          setExpanded((prev) => ({ ...prev, ...newExpanded }));
        }
      } catch (err) {
        toast.error("Failed to load folder contents");
        setCurrentFolderContents([]);
        setBreadcrumbPath([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFolderContents();
  }, [selectedId, items]);

  const handleFolderSelect = async (folderId) => {
    try {
      setLoading(true);
      const response = await getFolderContents(folderId);
      console.log("Folder Selected:", {
        folderId,
        folder: items.find((item) => item._id === folderId),
        contents: response.contents,
        path: response.path,
      });

      // Update items array with path information
      if (response.path && Array.isArray(response.path)) {
        const newItems = [...items];
        response.path.forEach((folder) => {
          const existingIndex = newItems.findIndex(
            (item) => item._id === folder._id
          );
          if (existingIndex === -1) {
            newItems.push(folder);
          } else {
            newItems[existingIndex] = folder;
          }
        });
        setItems(newItems);
      }

      setSelectedId(folderId);
      setCurrentFolderContents(response.contents);
      if (response.path && Array.isArray(response.path)) {
        setBreadcrumbPath(response.path);
        const newExpanded = {};
        response.path.forEach((folder) => {
          newExpanded[folder._id] = true;
        });
        setExpanded((prev) => ({ ...prev, ...newExpanded }));
      }
    } catch (err) {
      toast.error("Failed to load folder contents");
      setCurrentFolderContents([]);
      setBreadcrumbPath([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBreadcrumbClick = async (folderId) => {
    if (!folderId) {
      setSelectedId(null);
      setCurrentFolderContents(items.filter((item) => !item.parentId));
      setBreadcrumbPath([]);
      return;
    }

    try {
      setLoading(true);
      const response = await getFolderContents(folderId);
      setSelectedId(folderId);
      setCurrentFolderContents(response.contents);
      if (response.path && Array.isArray(response.path)) {
        setBreadcrumbPath(response.path);
        // Expand all parent folders in the path
        const newExpanded = {};
        response.path.forEach((folder) => {
          newExpanded[folder._id] = true;
        });
        setExpanded((prev) => ({ ...prev, ...newExpanded }));
      }
    } catch (err) {
      toast.error("Failed to load folder contents");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFolder = () => {
    // Get the folder from the path array instead of items
    console.log("breadcrumbPath", breadcrumbPath);
    // const selectedFolder =
    //   breadcrumbPath.length > 0
    //     ? breadcrumbPath[breadcrumbPath.length - 1]
    //     : items.find((item) => item._id === selectedId);
    const selectedFolder = breadcrumbPath[breadcrumbPath.length - 1];

    console.log("Selected Folder Object:", selectedFolder);
    if (selectedFolder) {
      onSelect({
        folder: selectedFolder,
        path: breadcrumbPath.map((item) => item.name).join(" / "),
      });
    }
    onClose();
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div>Error: {error}</div>;

  // Build the folder tree structure
  const folderTree = buildFolderTree(items);

  return (
    <div
      style={{
        display: "flex",
        height: "80vh",
        width: "100%",
        background: "white",
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      {/* Sidebar: Folder Tree */}
      <div
        style={{
          width: "280px",
          background: "rgba(255,255,255,0.95)",
          padding: "20px",
          borderRight: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            cursor: "pointer",
            padding: "8px 12px",
            borderRadius: "10px",
            marginBottom: "8px",
            fontWeight: !selectedId ? 600 : 400,
            color: !selectedId ? "#2563eb" : "#64748b",
            background: !selectedId ? "rgba(37,99,235,0.08)" : "transparent",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
          onClick={() => setSelectedId(null)}
        >
          <span style={{ fontSize: 18 }}>🏠</span>
          <span>Folders</span>
        </div>
        <FolderTree
          tree={folderTree}
          selectedId={selectedId}
          onSelect={handleFolderSelect}
          expanded={expanded}
          onToggle={(id) =>
            setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
          }
        />
      </div>

      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          padding: "20px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div>
            <h3 style={{ margin: "0 0 8px 0" }}>Select a Folder</h3>
            {/* Breadcrumbs */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.9em",
                color: "#6b7280",
              }}
            >
              <span
                style={{
                  cursor: "pointer",
                  color: selectedId ? "#6b7280" : "#2563eb",
                }}
                onClick={() => setSelectedId(null)}
              >
                Home
              </span>
              {breadcrumbPath.map((folder, index) => (
                <React.Fragment key={folder._id}>
                  <span style={{ color: "#6b7280" }}>/</span>
                  <span
                    style={{
                      cursor: "pointer",
                      color:
                        index === breadcrumbPath.length - 1
                          ? "#2563eb"
                          : "#6b7280",
                    }}
                    onClick={() => handleBreadcrumbClick(folder._id)}
                  >
                    {folder.name}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div>
            <button
              className="btn btn-secondary"
              onClick={onClose}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSelectFolder}
              disabled={!selectedId}
            >
              Select
            </button>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: "12px",
            overflowY: "auto",
            padding: "12px",
          }}
        >
          {currentFolderContents.map((item) => (
            <div
              key={item._id}
              style={{
                height: "110px",
                background: "white",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                padding: "12px 8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: item.type === "folder" ? "pointer" : "default",
                transition: "all 0.2s ease",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                ...(item.type === "folder" && {
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    borderColor: "#d1d5db",
                  },
                }),
              }}
              onClick={() =>
                item.type === "folder" && handleFolderSelect(item._id)
              }
            >
              <div
                style={{
                  fontSize: "28px",
                  marginBottom: "4px",
                  color: item.type === "folder" ? "#3b82f6" : "#6b7280",
                }}
              >
                {item.type === "folder" ? "📁" : getFileIcon(item.type)}
              </div>
              <div
                style={{
                  fontSize: "0.9em",
                  fontWeight: 500,
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  padding: "0 4px",
                  color: "#374151",
                }}
              >
                {item.name}
              </div>
              {item.type !== "folder" && (
                <div
                  style={{
                    fontSize: "0.75em",
                    color: "#6b7280",
                    backgroundColor: "#f3f4f6",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    marginTop: "4px",
                  }}
                >
                  {item.type.toUpperCase()}
                </div>
              )}
            </div>
          ))}
          {currentFolderContents.length === 0 && (
            <EmptyState icon="📂" title="No content in this folder." />
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectLibraryComponet;
